// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';
import { MultiSelect, useSnackBar } from '@usealloy/component-library';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';

import type { AttributeComponent } from 'flow/store/engine/types';
import {
  updateDataRouteValue,
  updateThreshold,
} from 'flow/store/redux/slice';
import { useAppDispatch } from '~/store/reduxHooks';

import { getMultiSelectProps } from './helpers';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import { MLAAttributeItemOrigin, RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { MLAEditorContext } from '../context';
import { CREATABLE_MLA_DROPDOWN_DATA_TYPES } from '../types';
import { CaretIndexType } from '../../useCaretControl/constants';
import {
  checkCurrencyValueFormatRequired,
  getAlphanumericCharacters,
  getMLAComponentOptions,
  getMLAFilterComponentLabel,
  getSpecificMLANoOptionsMessage,
} from '../../../../service/mla';
import { AttrComponentLabel } from '../styles';
import {
  DEFAULT_DECIMAL_MAX,
  DEFAULT_DECIMAL_MIN,
  DEFAULT_DOLLAR_MAX,
  DEFAULT_DOLLAR_MIN,
  DEFAULT_INTEGER_MAX,
  DEFAULT_INTEGER_MIN,
} from '../../thresholds/constants';
import { onEscKeyFromRuleDropdown } from '../../helpers';

function MLAComponentDropdown({
  token,
  componentPath,
  mlaData,
}: {
  token: {
    string: string;
    isOptional: boolean;
    caretIndex: number;
  };
  componentPath: string;
  mlaData: AttributeComponent[];
}) {
  const addSnackBar = useSnackBar();
  const appDispatch = useAppDispatch();
  // Flag for when a user tries to type something in that isn't a number or a
  // comma that will trigger the "NoOptionsComponent" to show with a prompt
  // telling them what they can input
  const [
    isInputInvalid,
    setIsInputInvalid,
  ] = React.useState(false);

  const multiSelectRef = useRef(null);

  const {
    morphingCaretPosition,
    setMorphingCaretPosition,
    getFlattenedCaretPositionIndex,
    setPendingMorphingCaretPositionUpdateValue,
    activeInputValue,
    setActiveInputValue,
    getPreviousCaretPosition,
    getNextCaretPosition,
    sourceData,
  } = useContext(MLAEditorContext);

  const mlaAttributeItem = useMemo(() => {
    const itemIndex = mlaData.findIndex(
      attr => attr.attributeComponent.precedence === token.caretIndex,
    );

    if (itemIndex < 0) return undefined;
    const item = mlaData[itemIndex];

    return {
      item,
      index: itemIndex,
      caretIndex: token.caretIndex,
    };
  }, [mlaData, token]);
  const componentInfo = mlaAttributeItem?.item?.attributeComponent;

  // flattened path meaning we convert path (e.g. [0][1]..) into 0-1 - which is the format we use for caret position / caret indices
  const flattenedPathInCaretPositionFormat = useMemo(() => (getFlattenedCaretPositionIndex({
    itemPath: String(mlaAttributeItem.caretIndex - 1),
    type: CaretIndexType.MLA,
  })), [getFlattenedCaretPositionIndex, mlaAttributeItem]);

  const mode = RuleEditorDropdownMode.MLA_COMPONENT;

  const isCurrencyValueRequired = useMemo(() => (
    checkCurrencyValueFormatRequired({
      component: componentInfo,
      attributeName: sourceData.attributeTokenMap.attribute.attribute_name,
    })
  ), [componentInfo, sourceData]);

  const options = React.useMemo<DropdownOption[]>(() => (
    isInputInvalid
      ? []
      : getMLAComponentOptions({
        component: componentInfo,
        isCurrencyValueRequired,
        configuredValue: mlaAttributeItem?.item?.value,
      })
  ), [
    componentInfo,
    isCurrencyValueRequired,
    isInputInvalid,
    mlaAttributeItem,
  ]);

  const selectedOptions = useMemo(() => {
    let incomingDropdownValue;
    incomingDropdownValue = mlaAttributeItem.item?.value;

    if (!incomingDropdownValue) return [];
    if (componentInfo.dataType === 'multi_select') {
      if (typeof incomingDropdownValue === 'string') {
        incomingDropdownValue = [incomingDropdownValue];
      }
      return options.filter(
        ({ value }) => incomingDropdownValue.find(
          attributeValue => String(attributeValue).toLowerCase() === value.toLowerCase()));
    }

    if (componentInfo.dataType === 'csv') {
      if (!Array.isArray(incomingDropdownValue)) {
        // usually if this is not an array, it's a malformed config. we just convert it into the right form
        return [{
          label: incomingDropdownValue,
          value: incomingDropdownValue,
        }];
      }

      const incomingValue = incomingDropdownValue.join(',');
      return [{
        label: incomingValue,
        value: incomingValue,
      }];
    }

    if (['select_one', 'integer', 'boolean', 'decimal'].includes(componentInfo.dataType)) {
      return options.filter(({ value }) => String(incomingDropdownValue) === value)
        .map(option => {
          const value = isCurrencyValueRequired
            ? (Number(option.value) / 100).toFixed(2)
            : option.value;
          return {
            label: isCurrencyValueRequired
              ? `$${value}`
              : option.label,
            value,
          };
        });
    }

    return [];
  }, [mlaAttributeItem, componentInfo, options, isCurrencyValueRequired]);

  const onChange = useCallback((inputValue: Option | Option[]) => {
    let finalComponentValue: string | number | (string | number)[];

    if (componentInfo.dataType === 'multi_select'
      && Array.isArray(inputValue)) {
      const allOptionExists = selectedOptions.find((item => item.value === 'all'));
      const userSelectedOptionOtherThanAll = inputValue.find((item => item.value !== 'all'));
      let selectedValues: Option[];
      if (userSelectedOptionOtherThanAll) {
        const userSelectedAll = inputValue.find((item => item.value === 'all'));
        if (allOptionExists) {
          // we filter "all" option from being saved because user selected another item than all
          selectedValues = inputValue.filter((item => item.value !== 'all'));
        } else if (userSelectedAll) {
          // user clicked "all" option, hence we just set "all" only
          selectedValues = inputValue.filter((item => item.value === 'all'));
        } else {
          selectedValues = inputValue;
        }
      } else {
        selectedValues = inputValue;
      }

      const selectedValuesWithoutLabel = selectedValues?.map(item => item.value);
      if (selectedValues.length === 0) {
        const allOptionInOptions = options.find((item => item.value === 'all'));
        if (allOptionInOptions && !componentInfo.isOptional) {
          finalComponentValue = allOptionInOptions.value;
        } else {
          finalComponentValue = undefined; // we delete this filter entirely
        }
      } else if (selectedValuesWithoutLabel.find(item => item === 'delete_filter')) {
        // this value will only be offered to optional filters
        finalComponentValue = undefined;
      } else {
        finalComponentValue = selectedValuesWithoutLabel;
      }
    } else {
      const {
        label, value, meta,
      } = inputValue as Option;
      if (meta && 'textInput' in meta && meta.textInput && !isNaN(Number(label))) {
        // ^ this is to satisfy TS compiler
        finalComponentValue = Number(label);
      }

      finalComponentValue = value;
      if (componentInfo.dataType === 'csv') {
        finalComponentValue = getAlphanumericCharacters(value);
      }

      if (componentInfo.dataType === 'integer' || componentInfo.dataType === 'decimal') {
        const possibleValues = typeof componentInfo.possibleValues === 'string'
          ? componentInfo.possibleValues.split(',')
          : [];
        // if the array length is 2, it means the possible values for this integer filter indicates min/max range
        const isPossibleValuesProvided = possibleValues.length === 2;

        const minimumByDataType = {
          integer: isPossibleValuesProvided ? Number(possibleValues[0]) : DEFAULT_INTEGER_MIN,
          decimal: isPossibleValuesProvided ? Number(possibleValues[0]) : DEFAULT_DECIMAL_MIN,
        };
        const maximumByDataType = {
          integer: isPossibleValuesProvided ? Number(possibleValues[1]) : DEFAULT_INTEGER_MAX,
          decimal: isPossibleValuesProvided ? Number(possibleValues[1]) : DEFAULT_DECIMAL_MAX,
        };

        const minValue = isCurrencyValueRequired
          ? DEFAULT_DOLLAR_MIN
          : minimumByDataType[componentInfo.dataType];
        const maxValue = isCurrencyValueRequired
          ? DEFAULT_DOLLAR_MAX
          : maximumByDataType[componentInfo.dataType];

        const isUserSelectedSameValue = selectedOptions[0]?.value === label;
        const integerValue = isUserSelectedSameValue
          ? Number(label)
          : Number(value);
        if (integerValue < minValue) {
          finalComponentValue = String(minValue);
          addSnackBar({
            title: 'The value was reset to the minimum value allowed for this filter.',
            type: 'warning',
          });
        } else if (integerValue > maxValue) {
          finalComponentValue = String(maxValue);
          addSnackBar({
            title: 'The value was reset to the maximum value allowed for this filter.',
            type: 'warning',
          });
        } else if (isCurrencyValueRequired) {
          let amount = parseFloat(label);
          amount = parseFloat(amount.toFixed(2));
          finalComponentValue = Math.round(amount * 100); // MLA dollar integer value should be formatted like this
        } else {
          finalComponentValue = String(integerValue);
        }
      }

      if (value === 'delete_filter') { // this value will only be offered to optional filters
        finalComponentValue = undefined;
      }
    }

    const payload = {
      path: `${componentPath}[${mlaAttributeItem?.index}].value`,
      value: finalComponentValue,
    };

    if (sourceData.type === MLAAttributeItemOrigin.DATAROUTE) {
      appDispatch(updateDataRouteValue({ ...payload }));
    } else {
      appDispatch(updateThreshold({ ...payload }));
    }

    if (componentInfo.dataType === 'multi_select'
      && (
        (
          Array.isArray(finalComponentValue)
          && !finalComponentValue.find(value => value === 'all')
        ) || finalComponentValue !== 'all'
      )
    ) return; // we don't move the cursor to the right side when user is clicking some items that are not "all"
    const { newPositionString, newPositionIndexForDelayedUpdate } = getNextCaretPosition({});

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  }, [
    componentInfo, appDispatch, componentPath, mlaAttributeItem,
    getNextCaretPosition, setPendingMorphingCaretPositionUpdateValue,
    setMorphingCaretPosition, sourceData, selectedOptions, addSnackBar,
    isCurrencyValueRequired, options,
  ]);

  const selectProps = useMemo(() => getMultiSelectProps(mode), [mode]);

  const updateValueUsingActiveInputValue = (inputValue: string) => {
    onChange({
      label: inputValue,
      value: inputValue,
      meta: {
        textInput: true,
      },
    });
    setActiveInputValue({
      value: '',
      path: '',
      initialValue: false,
    });
  };

  const onInputChange = (inputValue: string) => {
    // we track this to handle dropdown delete key down handling
    if (componentInfo.label === 'Entry Mode') {
      // This logic with this regex ensures that users can only input numbers
      // separated by commas
      if (!inputValue.match(/^(?:.*((\d+,|\d+)))*$/)) {
        setIsInputInvalid(true);
        return;
      }

      setIsInputInvalid(false);
    }

    if (inputValue.includes('.') && componentInfo.dataType === 'integer' && !isCurrencyValueRequired) return;
    setActiveInputValue({
      value: inputValue,
      path: componentPath || '',
      initialValue: false,
    });
  };

  const onDeleteItem = () => {
    // if activeInputValue has something, user should be able to delete indiv character in the dropdown box, not delete the whole item
    if (
      activeInputValue.value.length > 0
      || !mlaAttributeItem.item.attributeComponent.isOptional
    ) return;

    const payload = {
      path: `${componentPath}[${mlaAttributeItem?.index}].value`,
      value: undefined,
    };

    if (sourceData.type === MLAAttributeItemOrigin.DATAROUTE) {
      appDispatch(updateDataRouteValue({ ...payload }));
    } else {
      appDispatch(updateThreshold({ ...payload }));
    }

    const { newPositionString, newPositionIndexForDelayedUpdate } = getPreviousCaretPosition({
      userActionObject: {
        mode: 'deletion',
      },
    });

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  };

  const onEnterInputValueEditMode = () => {
    if (mode === RuleEditorDropdownMode.MLA_COMPONENT) {
      setActiveInputValue({
        value: selectedOptions[0]?.value || '',
        path: componentPath,
        initialValue: true,
      });
    }
  };

  const onFocus = () => {
    if (morphingCaretPosition !== flattenedPathInCaretPositionFormat
      && flattenedPathInCaretPositionFormat) {
      // update caret position to be here if focused by other ways (mouse click etc)
      setMorphingCaretPosition(`${flattenedPathInCaretPositionFormat}`);
      if (componentInfo.dataType === 'csv') {
        onEnterInputValueEditMode();
      }
    }
  };

  const onBlur = e => {
    if (activeInputValue.value.length > 0
      && ['integer', 'string', 'csv', 'decimal'].includes(componentInfo.dataType)) {
      // if there's any value provided by user, we'll automatically "submit" this for user convenience
      updateValueUsingActiveInputValue(activeInputValue.value);
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const isDropdownCreatable = useMemo(() => (
    isInputInvalid
      ? false
      : CREATABLE_MLA_DROPDOWN_DATA_TYPES.includes(componentInfo?.dataType)
  ), [componentInfo, isInputInvalid]);

  const inputValue = useMemo(() => {
    if (
      mode === RuleEditorDropdownMode.MLA_COMPONENT
      && activeInputValue.path === componentPath
      && morphingCaretPosition === flattenedPathInCaretPositionFormat
    ) {
      if (activeInputValue.value.length > 0
        || !activeInputValue.initialValue) {
        return activeInputValue.value;
      }

      return selectedOptions[0]?.value || '';
    }

    return undefined;
  }, [
    mode,
    activeInputValue,
    componentPath,
    morphingCaretPosition,
    flattenedPathInCaretPositionFormat,
    selectedOptions,
  ]);

  // these are hard coded labels because the backend data lacks.
  const label = useMemo(() => getMLAFilterComponentLabel({
    componentData: componentInfo,
  }), [componentInfo]);

  const userGuidanceMessage = useMemo(() => {
    // Check if there are any specific no options messages for this MLA component
    const specificNoOptionsMsg = getSpecificMLANoOptionsMessage(componentInfo);
    if (specificNoOptionsMsg) {
      return specificNoOptionsMsg;
    }
    // If there are no specific messages, use the default no options messages
    switch (componentInfo.dataType) {
      case 'integer': {
        if (isCurrencyValueRequired) {
          return 'Provide a dollar amount value';
        }
        return 'Provide an integer value';
      }
      case 'decimal': {
        if (isCurrencyValueRequired) {
          return 'Provide a dollar amount value';
        }
        return 'Provide a decimal value';
      }
      case 'csv':
        return 'Provide an alphanumerical value, separated by comma. No whitespace is allowed.';
    }

    return null;
  }, [componentInfo, isCurrencyValueRequired, getSpecificMLANoOptionsMessage]);

  const attrComponentValueConfigured = selectedOptions?.length > 0;
  const attrComponentOptionalAndNotConfigured = !attrComponentValueConfigured && token.isOptional;

  const customFilterOptions = useCallback(({ label: optionLabel }, input) => {
    if (options?.length <= 1 && !token.isOptional) {
      return false;
    }

    if (input) {
      return optionLabel?.toLowerCase().includes(input);
    }

    return true;
  }, [options, token]);

  const onEscKey = (_: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    onEscKeyFromRuleDropdown(
      e,
      activeInputValue,
      setActiveInputValue,
      multiSelectRef,
    );
  };

  useEffect(() => {
    if (morphingCaretPosition === flattenedPathInCaretPositionFormat) {
      // focus the dropdown automatically when caret position is pointing here
      multiSelectRef?.current?.focus();
    }
  }, [
    flattenedPathInCaretPositionFormat,
    mode,
    morphingCaretPosition,
  ]);

  return !attrComponentOptionalAndNotConfigured ? (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {attrComponentValueConfigured
          && label?.length > 0 && <AttrComponentLabel>{label}</AttrComponentLabel>}
        <MultiSelect
          {...selectProps}
          {...(componentInfo.label === 'Entry Mode' ? { autoFocus: true } : {})}
          openMenuOnFocus
          createOptionPosition="first"
          filterOption={customFilterOptions}
          onChange={onChange}
          options={options}
          onFocus={onFocus}
          onBlur={onBlur}
          inputValue={inputValue}
          selectedOptions={selectedOptions}
          isCreatable={isDropdownCreatable}
          isMulti={componentInfo?.dataType === 'multi_select'}
          placeholder={label}
          onCreateOption={input => onChange({
            ...input,
            meta: { ...input?.meta, textInput: true },
          })}
          onKeyDownHandlers={{
            Backspace: onDeleteItem,
            Delete: onDeleteItem,
            Alt: onEnterInputValueEditMode,
            Escape: onEscKey,
            Enter: onBlur,
          }}
          onInputChange={onInputChange}
          formatCreateLabel={newValue => (`Set value to ${newValue}`)}
          noOptionsMessage={() => userGuidanceMessage}
          ref={multiSelectRef}
        />
      </div>
    </div>
  ) : null;
}

export default MLAComponentDropdown;
